import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, FilePlus2 } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useClient, useClientActions } from './state';

export function ClientRegisterPage() {
  const snapshot = useClient((value) => value);
  const actions = useClientActions();
  const [clientName, setClientName] = useState('');
  const [fiduciaryName, setFiduciaryName] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');

  useEffect(() => {
    if (snapshot.matches('idle')) actions.loadClients();
  }, [actions, snapshot]);

  const submit = () => {
    const trimmedClient = clientName.trim();
    const trimmedFiduciary = fiduciaryName.trim();
    if (!trimmedClient || !trimmedFiduciary) return;
    actions.register(trimmedClient, trimmedFiduciary, beneficiaryName.trim() ? [beneficiaryName.trim()] : []);
    setClientName('');
    setFiduciaryName('');
    setBeneficiaryName('');
  };

  return (
    <AppShell title="Client register" activePath="/client-register">
      <div className="mx-auto max-w-3xl space-y-10 px-6 py-10">
        <header className="border-b border-border pb-6">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">New matter</p>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight">Register a client</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Create the client record that anchors future fiduciary filings.
          </p>
        </header>
        <section className="max-w-xl border border-border bg-card p-6">
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="client-name">Client name</Label>
              <Input
                data-jr-key="register-client-name"
                id="client-name"
                value={clientName}
                onChange={(event) => setClientName(event.target.value)}
                placeholder="Estate of ..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fiduciary-name">Fiduciary name</Label>
              <Input
                id="fiduciary-name"
                value={fiduciaryName}
                onChange={(event) => setFiduciaryName(event.target.value)}
                placeholder="Trustee or executor"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="beneficiary-name">Beneficiary (optional)</Label>
              <Input
                id="beneficiary-name"
                value={beneficiaryName}
                onChange={(event) => setBeneficiaryName(event.target.value)}
                placeholder="Beneficiary name"
              />
            </div>
            <Button
              data-jr-key="register-client"
              onClick={submit}
              disabled={snapshot.matches('registering') || !clientName.trim() || !fiduciaryName.trim()}
              className="gap-2"
            >
              <FilePlus2 />
              Register client
            </Button>
          </div>
        </section>
        <Link
          to="/clients"
          className="inline-flex items-center gap-2 text-sm text-primary underline-offset-4 hover:underline"
        >
          View clients <ArrowRight className="size-4" />
        </Link>
      </div>
    </AppShell>
  );
}
